import React from 'react';
import {
  AgnesPopover,
  AgnesPopoverTrigger,
  AgnesPopoverContent,
  AgnesButton,
  AgnesSelect,
  AgnesSelectTrigger,
  AgnesSelectValue,
  AgnesSelectContent,
  AgnesSelectItem,
} from '@agnes/ui';
import { Filter, Trash2, X } from 'lucide-react';
import {
  STEP_LABEL,
  CLASSIFICATION_LABEL,
  RISK_LABEL,
  type CboTableFiltros,
  type CboFiltrosOpcoes,
} from '@/lib/cbo';
import { labelFiltro, protegerSelectDoFechamento } from '@/lib/filtros';

type Opcao = { value: string; label: string };

const TRIGGER_CLS =
  'w-full min-w-0 [&>[data-slot=select-value]]:min-w-0 [&>[data-slot=select-value]]:flex-1 [&>[data-slot=select-value]]:truncate [&>[data-slot=select-value]]:text-left';

const optsFromLabels = (labels: Record<string, string>): Opcao[] =>
  Object.entries(labels).map(([value, label]) => ({ value, label }));

const strOpts = (valores: string[]): Opcao[] => valores.map((v) => ({ value: v, label: v }));

const SIM_NAO: Opcao[] = [
  { value: 'true', label: 'Sim' },
  { value: 'false', label: 'Não' },
];

const CAMPOS = [
  { key: 'etapa', placeholder: 'Etapa da análise', opcoes: () => optsFromLabels(STEP_LABEL) },
  {
    key: 'classificacao',
    placeholder: 'Classificação de análise',
    opcoes: () => optsFromLabels(CLASSIFICATION_LABEL),
  },
  { key: 'risco', placeholder: 'Grau de risco', opcoes: () => optsFromLabels(RISK_LABEL) },
  { key: 'ocupacao', placeholder: 'Ocupação', opcoes: (o: CboFiltrosOpcoes) => o.ocupacao },
  {
    key: 'ocupacaoRecomendada',
    placeholder: 'Ocupação (Recomendação)',
    opcoes: (o: CboFiltrosOpcoes) => o.ocupacao,
  },
  {
    key: 'descricaoAtividades',
    placeholder: 'Descrição de atividade',
    opcoes: (o: CboFiltrosOpcoes) => strOpts(o.descricaoAtividades),
  },
  {
    key: 'descricaoAtividadesRecomendada',
    placeholder: 'Descrição de atividade (Recomendação)',
    opcoes: (o: CboFiltrosOpcoes) => strOpts(o.descricaoAtividadesRecomendada),
  },
  { key: 'cargo', placeholder: 'Cargo', opcoes: (o: CboFiltrosOpcoes) => strOpts(o.cargo) },
  { key: 'setor', placeholder: 'Setor', opcoes: (o: CboFiltrosOpcoes) => strOpts(o.setor) },
  {
    key: 'ambienteS2240',
    placeholder: 'Ambiente de trabalho S-2240',
    opcoes: (o: CboFiltrosOpcoes) => strOpts(o.ambienteS2240),
  },
  {
    key: 'ambienteS2200S2206',
    placeholder: 'Ambiente de trabalho S-2200/S-2206',
    opcoes: (o: CboFiltrosOpcoes) => strOpts(o.ambienteS2200S2206),
  },
  {
    key: 'regimeTrabalhista',
    placeholder: 'Regime trabalhista',
    opcoes: (o: CboFiltrosOpcoes) => strOpts(o.regimeTrabalhista),
  },
  {
    key: 'grauInstrucao',
    placeholder: 'Grau de instrução',
    opcoes: (o: CboFiltrosOpcoes) => o.grauInstrucao,
  },
  {
    key: 'escolaridadeMinima',
    placeholder: 'Escolaridade mínima',
    opcoes: (o: CboFiltrosOpcoes) => o.escolaridadeMinima,
  },
  { key: 'cotaAprendizagem', placeholder: 'Cota de aprendizagem', opcoes: () => SIM_NAO },
  {
    key: 'orgaoClasse',
    placeholder: 'Órgão de classe',
    opcoes: (o: CboFiltrosOpcoes) => o.orgaoClasse,
  },
  { key: 'pcd', placeholder: 'PCD', opcoes: () => SIM_NAO },
  { key: 'categoria', placeholder: 'Categoria', opcoes: (o: CboFiltrosOpcoes) => o.categoria },
] as const;

type FiltroForm = Record<(typeof CAMPOS)[number]['key'], string>;

const VAZIO = Object.fromEntries(CAMPOS.map((c) => [c.key, ''])) as FiltroForm;

function toFiltros(f: FiltroForm): CboTableFiltros {
  const one = (v: string) => (v ? [v] : []);
  const oneNum = (v: string) => (v ? [Number(v)] : []);
  const oneBool = (v: string) => (v === '' ? null : v === 'true');
  return {
    step: one(f.etapa),
    status: [],
    classification: one(f.classificacao),
    risk: one(f.risco),
    analysisOccupationCode: one(f.ocupacaoRecomendada),
    analysisOccurrenceId: [],
    occupationCode: one(f.ocupacao),
    activityDescription: one(f.descricaoAtividades),
    activityDescriptionRecommended: one(f.descricaoAtividadesRecomendada),
    cargo: one(f.cargo),
    setor: one(f.setor),
    nociveAgentEnvironment: one(f.ambienteS2240),
    legalAgreementEnvironment: one(f.ambienteS2200S2206),
    regimeTrabalhista: one(f.regimeTrabalhista),
    grauInstrucao: one(f.grauInstrucao),
    escolaridadeMinima: oneNum(f.escolaridadeMinima),
    cotaAprendizagem: oneBool(f.cotaAprendizagem),
    orgaoClasse: oneNum(f.orgaoClasse),
    pcd: oneBool(f.pcd),
    categoria: oneNum(f.categoria),
  };
}

type Props = {
  opcoes: CboFiltrosOpcoes;
  onApply: (f: CboTableFiltros) => void;
  count?: number;
};

type SelectFieldProps = {
  value: string;
  onValueChange: (v: string) => void;
  placeholder: string;
  options: Opcao[];
  dataQa: string;
};
function SelectField({ value, onValueChange, placeholder, options, dataQa }: SelectFieldProps) {
  const selectedLabel = options.find((option) => option.value === value)?.label;

  return (
    <AgnesSelect value={value} onValueChange={onValueChange}>
      <AgnesSelectTrigger className={TRIGGER_CLS} dataQa={dataQa}>
        <AgnesSelectValue placeholder={placeholder} title={selectedLabel} />
      </AgnesSelectTrigger>
      <AgnesSelectContent
        position="popper"
        dataQa={`${dataQa}-lista`}
        className="w-[var(--radix-select-trigger-width)] max-w-[calc(100vw-24px)]"
      >
        {options.map((o) => (
          <AgnesSelectItem
            key={o.value}
            value={o.value}
            dataQa={dataQa}
            className="[&>span:last-child]:min-w-0"
          >
            <span className="block w-full min-w-0 truncate" title={o.label}>
              {o.label}
            </span>
          </AgnesSelectItem>
        ))}
      </AgnesSelectContent>
    </AgnesSelect>
  );
}

export default function CboFilter({ opcoes, onApply, count = 0 }: Props) {
  const [open, setOpen] = React.useState(false);
  const [f, setF] = React.useState<FiltroForm>(VAZIO);
  const set = (k: keyof FiltroForm) => (v: string) => setF((s) => ({ ...s, [k]: v }));

  const aplicar = () => {
    onApply(toFiltros(f));
    setOpen(false);
  };
  const limpar = () => {
    setF(VAZIO);
    onApply(toFiltros(VAZIO));
  };

  return (
    <AgnesPopover open={open} onOpenChange={setOpen}>
      <AgnesPopoverTrigger asChild>
        <AgnesButton
          variant="outlined"
          startIcon={<Filter className="h-4 w-4" data-qa="cbo-filtro-icone-filtro" />}
          data-qa="cbo-filtro-trigger"
          aria-label={count > 0 ? `Filtro, ${count} ativos` : 'Filtro'}
        >
          {labelFiltro('Filtro', count)}
        </AgnesButton>
      </AgnesPopoverTrigger>

      <AgnesPopoverContent
        align="end"
        className="w-80 max-w-[calc(100vw-24px)] p-0"
        dataQa="cbo-filtro-popover"
        onInteractOutside={protegerSelectDoFechamento}
      >
        <div
          data-qa="cbo-filtro-cabecalho"
          className="flex items-center justify-between border-b border-agnes-border px-4 py-3"
        >
          <span data-qa="cbo-filtro-titulo" className="text-base font-semibold text-agnes-text">
            Filtro
          </span>
          <button
            type="button"
            data-qa="cbo-filtro-fechar"
            aria-label="Fechar"
            onClick={() => setOpen(false)}
            className="text-agnes-text-muted hover:text-agnes-text"
          >
            <X className="h-5 w-5" data-qa="cbo-filtro-fechar-icone" />
          </button>
        </div>

        <div
          data-qa="cbo-filtro-campos"
          className="flex min-w-0 max-h-[60vh] flex-col gap-3 overflow-y-auto px-4 py-4 [&>*]:shrink-0"
        >
          {CAMPOS.map((c) => (
            <SelectField
              key={c.key}
              value={f[c.key]}
              onValueChange={set(c.key)}
              dataQa={`cbo-filtro-campo-${c.key}`}
              placeholder={c.placeholder}
              options={c.opcoes(opcoes)}
            />
          ))}
        </div>

        <div
          data-qa="cbo-filtro-acoes"
          className="flex flex-col gap-2 border-t border-agnes-border px-4 py-3"
        >
          <AgnesButton
            variant="primary"
            onClick={aplicar}
            className="w-full"
            data-qa="cbo-filtro-aplicar"
          >
            Aplicar filtro
          </AgnesButton>
          <AgnesButton
            variant="outlined"
            onClick={limpar}
            data-qa="cbo-filtro-limpar"
            startIcon={<Trash2 className="h-4 w-4" data-qa="cbo-filtro-limpar-icone" />}
            className="w-full"
          >
            Limpar filtro
          </AgnesButton>
        </div>
      </AgnesPopoverContent>
    </AgnesPopover>
  );
}
